import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormInput } from "@/components/FormFields/FormInput";
import { FormSelect } from "@/components/FormFields/FormSelect";
import { FormTextarea } from "@/components/FormFields/FormTextarea";
import { FormCheckbox } from "@/components/FormFields/FormCheckbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  consultationFormSchema,
  type ConsultationFormValues,
  timezoneOptions,
  timeSlotOptions,
} from "./formSchema";

export function ConsultationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  
  const form = useForm<ConsultationFormValues>({
    resolver: zodResolver(consultationFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      message: "",
      consultationDate: "",
      timezone: "",
      consent: false,
    },
  });

  async function onSubmit(data: ConsultationFormValues) {
    setIsSubmitting(true);
    
    try {
      // Format the data for submission
      const payload = {
        ...data,
        submittedAt: new Date().toISOString(),
      };
      
      const webhookUrl = import.meta.env.VITE_CONSULTATION_WEBHOOK_URL;
      
      if (!webhookUrl) {
        throw new Error("Consultation webhook URL not configured - please set VITE_CONSULTATION_WEBHOOK_URL in your environment variables");
      }
      
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        mode: "no-cors", // Required for cross-origin webhook calls
      });
      
      // Since we're using no-cors, we won't get a proper response status
      // Instead, we'll assume success and inform the user
      toast({
        title: "Consultation Request Submitted",
        description: "We'll contact you shortly to confirm your appointment.",
      });
      
      // Reset the form after successful submission
      form.reset();
      setSelectedDate(undefined);
      setSelectedTime(null);
      
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Submission Error",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  // Handle date and time selection
  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date && selectedTime) {
      const formattedDate = format(date, "yyyy-MM-dd");
      form.setValue("consultationDate", `${formattedDate} ${selectedTime}`);
    }
  };
  
  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    
    if (selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      form.setValue("consultationDate", `${formattedDate} ${time}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Fields (side by side) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            form={form}
            name="firstName"
            label="First Name"
            placeholder="Enter your first name"
          />
          <FormInput
            form={form}
            name="lastName"
            label="Last Name"
            placeholder="Enter your last name"
          />
        </div>

        {/* Email */}
        <FormInput
          form={form}
          name="email"
          label="Work Email"
          placeholder="your.name@company.com"
          type="email"
        />

        {/* Company */}
        <FormInput
          form={form}
          name="company"
          label="Company Name"
          placeholder="Enter your company name"
        />

        {/* Discussion Message */}
        <FormTextarea
          form={form}
          name="message"
          label="What Would You Like to Discuss?"
          placeholder="Please share any specific topics, challenges, or questions you'd like to address during the consultation..."
          maxLength={500}
          showCount={true}
        />

        {/* Date and Time Selection */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="consultationDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Preferred Date & Time</FormLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Date Picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateChange}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Time Picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !selectedTime && "text-muted-foreground"
                          )}
                        >
                          <Clock className="mr-2 h-4 w-4" />
                          {selectedTime ? timeSlotOptions.find(t => t.value === selectedTime)?.label : "Select time"}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="start">
                      <div className="grid gap-1">
                        {timeSlotOptions.map((timeOption) => (
                          <Button
                            key={timeOption.value}
                            variant="ghost"
                            className={cn(
                              "justify-start font-normal",
                              selectedTime === timeOption.value && "bg-primary/20"
                            )}
                            onClick={() => handleTimeChange(timeOption.value)}
                          >
                            {timeOption.label}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Timezone Selection */}
          <FormSelect
            form={form}
            name="timezone"
            label="Timezone"
            placeholder="Select your timezone"
            options={timezoneOptions}
          />
        </div>

        {/* Consent Checkbox */}
        <FormCheckbox form={form} name="consent">
          I agree to be contacted about my consultation request and receive relevant communications 
          about AI services for legal professionals. <a href="/privacy-policy" className="text-primary hover:underline">Privacy policy</a>.
        </FormCheckbox>

        {/* Submit Button */}
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full py-6 text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-primary/20"
        >
          {isSubmitting ? "Submitting..." : "Book My Consultation"}
        </Button>
      </form>
    </Form>
  );
}
